import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
} from 'react';
import { FieldLabel, parseFieldLabel } from '@/components/ui/FieldLabel';
import { CountryFlag } from '@/components/ui/CountryFlag';
import { useDefaultPhoneCountry } from '@/hooks/useDefaultPhoneCountry';
import { cn } from '@/utils/cn';
import { getPhoneCountry, filterPhoneCountries } from '@/utils/phoneCountries';
import {
  buildE164,
  parsePhoneValue,
  sanitizeNationalInput,
  validateNationalNumber,
} from '@/utils/phoneNumber';

export interface PhoneInputProps {
  label?: string;
  value?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  name?: string;
  id?: string;
  error?: string;
  hint?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  autoFocus?: boolean;
  placeholder?: string;
  /** Override org/browser default country (ISO code, e.g. IN). */
  defaultCountryCode?: string;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  {
    label,
    value = '',
    onChange,
    onBlur,
    name,
    id,
    error,
    hint,
    disabled,
    required,
    className,
    autoFocus,
    placeholder,
    defaultCountryCode,
    onKeyDown,
  },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? name ?? generatedId;
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const { required: isRequired } = parseFieldLabel(label, required);
  const resolvedDefaultCountry = useDefaultPhoneCountry(defaultCountryCode);

  const parsed = useMemo(
    () => parsePhoneValue(value, resolvedDefaultCountry),
    [value, resolvedDefaultCountry],
  );

  const [countryCode, setCountryCode] = useState(parsed.countryCode);
  const [national, setNational] = useState(parsed.national);
  const [localError, setLocalError] = useState<string | undefined>();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const lastEmitted = useRef(value);

  useEffect(() => {
    if (value === lastEmitted.current) return;
    const next = parsePhoneValue(value, resolvedDefaultCountry);
    setCountryCode(next.countryCode);
    setNational(next.national);
    lastEmitted.current = value;
  }, [value, resolvedDefaultCountry]);

  useEffect(() => {
    if (!pickerOpen) return;
    const onDocClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setPickerOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [pickerOpen]);

  useEffect(() => {
    if (pickerOpen) {
      searchRef.current?.focus();
    }
  }, [pickerOpen]);

  const country = getPhoneCountry(countryCode);
  const displayError = error ?? localError;

  const filteredCountries = useMemo(() => filterPhoneCountries(search), [search]);

  const emitChange = useCallback(
    (nextCountry: string, nextNational: string) => {
      const e164 = buildE164(nextCountry, nextNational);
      lastEmitted.current = e164;
      onChange?.({
        target: { value: e164, name: name ?? '' },
      } as ChangeEvent<HTMLInputElement>);
    },
    [name, onChange],
  );

  const handleCountryChange = (nextCountry: string) => {
    setCountryCode(nextCountry);
    const trimmedNational = sanitizeNationalInput(nextCountry, national);
    setNational(trimmedNational);
    setLocalError(undefined);
    emitChange(nextCountry, trimmedNational);
    setPickerOpen(false);
    setSearch('');
  };

  const handleNationalChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextNational = sanitizeNationalInput(countryCode, event.target.value);
    setNational(nextNational);
    setLocalError(undefined);
    emitChange(countryCode, nextNational);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (national) {
      setLocalError(validateNationalNumber(countryCode, national));
    } else {
      setLocalError(undefined);
    }
    onBlur?.(event);
  };

  return (
    <div className={cn('w-full', className)} ref={rootRef}>
      <FieldLabel htmlFor={inputId} label={label} required={required} />
      <div className="relative">
        <div
          className={cn(
            'input flex items-stretch gap-0 p-0',
            displayError && 'input-error',
            disabled && 'cursor-not-allowed opacity-60',
          )}
        >
          <button
          type="button"
          disabled={disabled}
          aria-label={`${country.name}, +${country.dialCode || ''}. Change country`}
          aria-haspopup="listbox"
          aria-expanded={pickerOpen}
          onClick={() => {
            if (!disabled) setPickerOpen((open) => !open);
          }}
          className={cn(
            'flex shrink-0 items-center gap-1 rounded-l-lg py-2 pl-2.5 pr-2',
            'text-slate-800 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-slate-800/60',
            disabled && 'pointer-events-none',
          )}
        >
          <CountryFlag code={country.code} variant="field" />
          <KeyboardArrowDownIcon
            sx={{ fontSize: 18 }}
            className={cn('text-slate-500 transition-transform', pickerOpen && 'rotate-180')}
          />
          <span className="text-sm font-medium tabular-nums">
            {country.dialCode ? `+${country.dialCode}` : '+'}
          </span>
        </button>

        <div className="my-2 w-px shrink-0 self-stretch bg-slate-200 dark:bg-slate-600" aria-hidden />

        <input
          ref={ref}
          id={inputId}
          name={name}
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          autoFocus={autoFocus}
          disabled={disabled}
          required={isRequired}
          value={national}
          maxLength={country.nationalLength}
          placeholder={placeholder}
          onChange={handleNationalChange}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          aria-invalid={displayError ? true : undefined}
          aria-describedby={displayError ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
          className="min-w-0 flex-1 border-0 bg-transparent px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-0 dark:text-white dark:placeholder:text-slate-400"
        />
        </div>

        {pickerOpen ? (
          <div
            role="listbox"
            aria-label="Select country"
            className="absolute left-0 top-[calc(100%+4px)] z-50 w-80 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900"
            onMouseDown={(e) => e.preventDefault()}
          >
            <div className="border-b border-slate-200 p-2 dark:border-slate-700">
              <div className="relative">
                <SearchIcon
                  sx={{ fontSize: 16 }}
                  className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Escape') {
                      setPickerOpen(false);
                      setSearch('');
                    }
                  }}
                  placeholder="Search country or code…"
                  autoComplete="off"
                  className="w-full rounded-md border border-slate-200 bg-white py-1.5 pl-8 pr-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500/30 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                />
              </div>
            </div>
            <ul className="max-h-56 overflow-y-auto py-1">
              {filteredCountries.length === 0 ? (
                <li className="px-3 py-2 text-sm text-slate-500">No matches</li>
              ) : (
                filteredCountries.map((c) => (
                  <li key={c.code}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={c.code === countryCode}
                      onClick={() => handleCountryChange(c.code)}
                      className={cn(
                        'flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800',
                        c.code === countryCode && 'bg-primary-50 text-primary-800 dark:bg-primary-950/40 dark:text-primary-200',
                      )}
                    >
                      <CountryFlag code={c.code} variant="list" />
                      <span className="min-w-0 flex-1 truncate font-medium text-slate-800 dark:text-slate-100">
                        {c.name}
                      </span>
                      <span className="shrink-0 tabular-nums text-slate-500 dark:text-slate-400">
                        +{c.dialCode}
                      </span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        ) : null}
      </div>
      {hint && !displayError ? (
        <p id={`${inputId}-hint`} className="mt-1 text-xs text-slate-500">
          {hint}
        </p>
      ) : null}
      {displayError ? (
        <p id={`${inputId}-error`} className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
          {displayError}
        </p>
      ) : null}
    </div>
  );
});

PhoneInput.displayName = 'PhoneInput';
